import { HomeFilled, UpOutlined, DownOutlined } from '@ant-design/icons';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import React, { useEffect, useState, useRef} from 'react';

import { useClickAway } from 'common/hooks';
import InputSearch from './input-search';
import NewCheckBoxGroup from './new-checkbox-group';
import { FilterProperty } from '~components/svgs';

const useStyles = makeStyles(theme => ({
  header: {
    backgroundColor: theme.colors.gray1,
    padding: '5px 13px 5px 15px',
    border: 'none',
    border: '1px solid',
    borderColor: theme.colors.gray5,
    boxShadow: ' 0px 2px 0px 0px #00000004',
    borderRadius: theme.borderRadius.min,
    ...theme.typography.body2,
    '&:hover': {
      cursor: 'pointer',
      borderColor: theme.colors.mainGreen5,
    },
  },
  noborderHeader: {
    border: 'none',
    padding: '9px 8px',
    ...theme.typography.body2,
    color: theme.colors.bodyColor,
    ...theme.flex.crossCenter,
    '&:hover': {
      cursor: 'pointer',
      color: theme.colors.bodyColor,
      background: theme.colors.gray3,
    },
  },
  afterClick: {
    boxShadow: '0px 0px 0px 2px #1890FF33',
  },
  selectColor: {
    color: `${theme.colors.mainGreen6} !important`,
  },
  homeSvg: {
    marginRight: '7px',
  },
  plotSvg: {
    width: '13px',
    marginRight: '9px',
    color: theme.colors.gray10,
    verticalAlign: 'middle',
    fill: theme.colors.bodyColor,
  },
  ploSvgFill: {
    fill: theme.colors.mainGreen6,
  },
  downSvg: {
    verticalAlign: 'middle',
    marginLeft: '8px',
    fontSize: '8px !important',
  },
  searchBox: {
    position: 'relative',
    display: 'inline-block',
  },
  filterTable: {
    backgroundColor: theme.colors.gray1,
    borderRadius: theme.borderRadius.min,
    display: 'block',
    position: 'absolute',
    boxShadow: '0px 9px 28px 8px #0000000D',
    top: '37px',
    zIndex: 3,

    '& .ant-checkbox-group': {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  hideFilterBox: {
    display: 'none',
  },
  bottomBox: {
    ...theme.flex.center,
    justifyContent: 'space-between',
    borderTop: '1px solid #E8E8E8',
    padding: '10px 12px',
  },
  resetBtn: {
    cursor: 'pointer',
    fontSize: '12px',
    lineHeight: '22px',
    border: 'none',
    marginRight: '12px',
    borderRadius: '2px',
    paddingLeft: '8px',
    paddingRight: '8px',
    '&:hover': {
      backgroundColor: theme.colors.gray3,
    },
  },
  okbtn: {
    cursor: 'pointer',
    border: 'none',
    padding: '1px 8px',
    background: '#38B2A6',
    fontSize: '12px',
    lineHeight: '22px',
    color: theme.colors.gray1,
    borderRadius: '2px',
    ...theme.flex.center,
  },
  disabledbtn: {
    color: theme.colors.disableColor,
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
}));

const ConfirmBar = ({ disabled, resetOnClick, onOkClick }) => {
  const classes = useStyles();
  return (
    <div className={classes.bottomBox}>
      <button
        className={classNames(classes.resetBtn, { [classes.disabledbtn]: disabled })}
        onClick={resetOnClick}
        disabled={disabled}
      >
        Reset
      </button>
      <button className={classes.okbtn} onClick={onOkClick}>
        OK
      </button>
    </div>
  );
};

const SelectedBar = ({
  onClick,
  checkedFilters,
  placeholderInfo,
  isShowFilterTable,
  hasNum = true,
  hasBorder,
  hasArrow,
  prefixIcon = 'plot',
  mode,
  btnClassName
}) => {
  const { title, filteredTitle } = placeholderInfo;

  const classes = useStyles();

  const iconMap = {
    home: <HomeFilled className={classes.homeSvg} />,
    plot: (
      <FilterProperty
        className={classNames(classes.plotSvg, {
          [classes.ploSvgFill]: checkedFilters.length > 0,
        })}
      />
    ),
    up: <UpOutlined className={classes.downSvg} />,
    down: <DownOutlined className={classes.downSvg} />,
  };

  const prefix = typeof prefixIcon === 'string' ? iconMap[prefixIcon] : prefixIcon;

  if (mode === 'reconciliationSearch') {
    return (
    <Button
      onClick={onClick}
      className={classNames({
        [classes.selectColor]: checkedFilters.length > 0,
      },btnClassName)}
    >
      {prefix}
      {checkedFilters.length === 0
        ? title
        : `${filteredTitle}${hasNum ? ' ·' + checkedFilters.length : ''}`}
      <If condition={hasArrow}>{iconMap[isShowFilterTable ? 'up' : 'down']}</If>
    </Button>
  );
  }
  return (
    <button
      onClick={onClick}
      className={classNames({
        [classes.header]: hasBorder,
        [classes.noborderHeader]: !hasBorder,
        [classes.selectColor]: checkedFilters.length > 0,
      })}
    >
      {prefix}
      {checkedFilters.length === 0
        ? title
        : `${filteredTitle}${hasNum ? ' ·' + checkedFilters.length : ''}`}
      <If condition={hasArrow}>{iconMap[isShowFilterTable ? 'up' : 'down']}</If>
    </button>
  );
};

const FilterTable = ({
  placeholder,
  dataSource,
  checkedFilters,
  setCheckedFilters,
  isShowFilterTable,
  setShowFilterTable,
  noSearch,
  onChange,
  filterTableClassName,
  mode
}) => {
  const classes = useStyles();
  const [checkBoxData, setCheckBoxData] = useState(dataSource);
  const [checkedList, setCheckedList] = useState(checkedFilters ? checkedFilters : []);

  const OnSearch = searchInfo => {
    let searchResults = dataSource;

    const fuseOptions = {
      includeScore: true,
      keys: ['label'],
    };

    const fuseSearch = new Fuse(dataSource, fuseOptions);

    if (searchInfo !== '') {
      searchResults = fuseSearch?.search(searchInfo)?.map(option => option.item);
    }

    setCheckBoxData(searchResults);
    setCheckedList([]);
  };

  const resetOnClick = () => {
    setCheckedList([]);
    setShowFilterTable(false);
    onChange([]);
  };

  const onOkClick = () => {
    setCheckedFilters(checkedList);
    setShowFilterTable(false);
    onChange(checkedList);
  };

  useEffect(() => {
    if (isShowFilterTable) {
      setCheckedList(checkedFilters);
      setCheckBoxData(dataSource);
    }
  }, [isShowFilterTable]);

  return (
    <If condition={isShowFilterTable}>
      <div className={classNames(classes.filterTable,filterTableClassName)}>
        <If condition={!noSearch}>
          <InputSearch onChange={OnSearch} placeholder={placeholder} value="" />
        </If>

        <NewCheckBoxGroup
          options={checkBoxData}
          value={checkedList}
          mode={mode}
          onChange={checkedList => {
            setCheckedList(checkedList);
          }}
        />

        <ConfirmBar
          resetOnClick={resetOnClick}
          onOkClick={onOkClick}
          disabled={checkedList.length === 0}
        />
      </div>
    </If>
  );
};

const NewSearch = props => {
  const NewSearchRef = useRef(null);

  const classes = useStyles();
  const {
    options,
    className,
    onChange,
    defaultCheckedFilters,
    noSearch,
    placeholderInfo,
    prefixIcon,
    hasArrow,
    hasBorder,
    hasNum,
    mode,
    btnClassName,
    filterTableClassName
  } = props;

  const [isShowFilterTable, setShowFilterTable] = useState(false);
  const [checkedFilters, setCheckedFilters] = useState(
    defaultCheckedFilters ? defaultCheckedFilters : [],
  );

  const handleHeaderClick = () => {
    props?.onClick?.();
    setShowFilterTable(!isShowFilterTable);
  };

  useEffect(() => {
    setCheckedFilters(defaultCheckedFilters);
  }, [defaultCheckedFilters]);

  useClickAway(
    NewSearchRef,
    () => {
      setShowFilterTable(false);
    },
    ['mousedown'],
  );

  return (
    <div className={classNames(classes.searchBox, className)} ref={NewSearchRef}>
      <SelectedBar
        prefixIcon={prefixIcon}
        hasArrow={hasArrow}
        hasBorder={hasBorder}
        hasNum={hasNum}
        placeholderInfo={placeholderInfo}
        checkedFilters={checkedFilters}
        isShowFilterTable={isShowFilterTable}
        onClick={handleHeaderClick}
        mode={mode}
        btnClassName={btnClassName}
      />
      <FilterTable
        dataSource={options}
        checkedFilters={checkedFilters}
        setCheckedFilters={setCheckedFilters}
        isShowFilterTable={isShowFilterTable}
        setShowFilterTable={setShowFilterTable}
        noSearch={noSearch ? true : false}
        onChange={onChange}
        filterTableClassName={filterTableClassName}
        mode={mode}
      />
    </div>
  );
};

NewSearch.propTypes = {
  prefixIcon: PropTypes.string,
  noSearch: PropTypes.bool,
  hasArrow: PropTypes.bool,
  hasBorder: PropTypes.bool,
  hasNum: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ),
  onClick: PropTypes.func,
  onChange: PropTypes.func,
  defaultCheckedFilters: PropTypes.array,
  placeholderInfo: PropTypes.object.isRequired,
};

export default NewSearch;
